import type { DecidedSignal, DecidedSource } from "../api/types";
import { parseSeriesSlug } from "./series";
import { formatDollars } from "./tape";

/**
 * Разбор теневого сигнала «покупка почти решённого» для экрана.
 *
 * # Что этот раздел обязан сказать, и почему это не «сколько заработали»
 *
 * Подсистема воспроизводит архетип, найденный переписью контрагентов: 22
 * кошелька, $120 тыс. за 16 суток, средняя цена входа 0.990, доля выигранных
 * окон у медианного участника 1.00. Мы входим ДЕШЕВЛЕ — ради маржи в пять
 * центов вместо одного, — и весь вопрос в том, достаётся ли по такой цене
 * хоть что-нибудь.
 *
 * Поэтому главная пара чисел здесь — не прибыль, а ОТОБРАНО и ИСПОЛНИМО. Отбор
 * окон от цены входа не зависит вовсе (замок считает исход, а не цену), а
 * исполнимость зависит целиком. Показав только первое, панель врала бы:
 * «сигналов много» читалось бы как «сделок много».
 */

/** Число из строки контракта. null означает прочерк, а не ноль. */
function num(v: string | null | undefined): number | null {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/**
 * Короткое число share.
 *
 * Тысячи сокращаются, сотые остаются до сотни. Порог здесь не косметический:
 * в одной колонке живут очередь впереди (десятки тысяч — там сотые не значат
 * ничего) и размер нашей заявки (десятки — там сотая доля это тридцать центов,
 * и округлять её значило бы показать не тот ордер).
 */
export function shares(v: string | null | undefined): string {
  const n = num(v);
  if (n === null) return "—";
  if (Math.abs(n) >= 10_000) return `${Math.round(n / 1000)}k`;
  if (Math.abs(n) >= 1000) return `${(n / 1000).toFixed(1)}k`;
  if (Math.abs(n) >= 100) return n.toFixed(0);
  return n.toFixed(2);
}

/** Фора в секундах. Знаковая: рынок открыт и после конца окна. */
export function lead(ms: number | null | undefined): string {
  if (ms === null || ms === undefined) return "—";
  return `${(ms / 1000).toFixed(1)} с`;
}

/** Имя источника словами: `pm` и `model` ничего не говорят стороннему глазу. */
export function sourceLabel(source: string): string {
  if (source === "pm") return "ряд биржи";
  if (source === "model") return "наша модель";
  return source;
}

/** Пояснение источника — то, чем он отличается, а не как называется. */
export function sourceHint(source: string): string {
  if (source === "pm") {
    return "исход посчитан по ряду, который опубликовал Polymarket: база отсчёта, никакой модели";
  }
  if (source === "model") {
    return "тот же расчёт по ряду, достроенному спотом биржи: те же секунды на 1–3 с раньше";
  }
  return "";
}

/**
 * Ступени лесенки цен, от дорогой к дешёвой.
 *
 * Сводка режется по цене, потому что цена не влияет на отбор окон, но целиком
 * определяет исполнимость: свод по двум ценам сразу дал бы среднее между двумя
 * разными опытами.
 *
 * Дорогая первой, а не дешёвая: 0.99 — цена самого архетипа, база отсчёта, с
 * которой сравнивают остальные. Разрез читают сверху вниз.
 */
export function pricesOf(sources: DecidedSource[]): string[] {
  const seen = new Set(sources.map((s) => s.limit_price));
  return [...seen].sort((a, b) => Number(b) - Number(a));
}

/**
 * Строки одного источника, от дорогой ступени к дешёвой.
 *
 * Отдельно от atPrice, потому что вопросы противоположные: atPrice отвечает
 * «что было по этой цене у обоих источников», а этот — «как выглядит ЛЕСЕНКА
 * одного источника», и только второй позволяет сравнивать ступени.
 */
export function atSource(sources: DecidedSource[], source: string): DecidedSource[] {
  return sources
    .filter((s) => s.source === source)
    .sort((a, b) => Number(b.limit_price) - Number(a.limit_price));
}

/** Источники, встреченные в сводке, в порядке появления. */
export function sourcesOf(sources: DecidedSource[]): string[] {
  return [...new Set(sources.map((s) => s.source))];
}

/**
 * СОПОСТАВИМЫ ли ступени лесенки между собой — и почему, если нет.
 *
 * # Ради чего эта строка вообще существует
 *
 * Ровно ради того вопроса, который вызвал всю правку. До лесенки цена была одним
 * числом в настройке: подсистема писала по одной цене за раз, и смена настройки
 * резала журнал не на два опыта, а на два непересекающихся отрезка ВРЕМЕНИ. В
 * сводке они вставали рядом — «0.95: 30 окон, 0.99: 1009 окон», — и читались как
 * сравнение цен, хотя различались календарём: другие сутки, другая погода,
 * другие серии, другая глубина стакана.
 *
 * Теперь строки пишутся веером по всем ступеням с одного решения, поэтому у
 * настоящей лесенки число отобранных окон СОВПАДАЕТ у всех ступеней одного
 * источника. Расхождение означает ровно одно: ступени набраны на разных окнах, и
 * разность между ними — не цена лишнего цента.
 *
 * Проверка обязана жить на экране, а не в голове оператора: старые
 * одноступенчатые строки не удаляются (измеренного не удаляют) и живут рядом с
 * лесенкой весь срок хранения. Отличить их иначе можно только запросом в базу.
 */
export function ladderWord(rows: DecidedSource[]): string {
  const first = rows[0];
  if (first === undefined) return "ступеней нет";
  if (rows.length === 1) {
    return `одна ступень (${first.limit_price}): сравнивать не с чем`;
  }
  const same = rows.every((r) => r.signals === first.signals);
  const steps = rows.map((r) => r.limit_price).join(" / ");
  if (same) {
    return `лесенка ${steps} на одних окнах (${first.signals}): разница между ступенями — ЦЕНА`;
  }
  const spread = rows.map((r) => `${r.limit_price}: ${r.signals}`).join(", ");
  return (
    `ступени набраны на РАЗНЫХ окнах (${spread}) — сравнивать между собой нельзя: ` +
    `разница в календаре, а не в цене`
  );
}

/**
 * Цвет приговора лесенке.
 *
 * Расхождение красится ПРЕДУПРЕЖДЕНИЕМ, а не ошибкой: сводка при этом верна по
 * каждой строке, неверно только чтение её как сравнения цен. Одна ступень —
 * серым: это не поломка, а «лесенка ещё не набралась» либо срез старых строк.
 */
export function ladderTone(rows: DecidedSource[]): string {
  const first = rows[0];
  if (first === undefined || rows.length < 2) return "var(--color-muted)";
  return rows.every((r) => r.signals === first.signals)
    ? "var(--color-good)"
    : "var(--color-warn)";
}

/**
 * Приговор ВСЕМ лесенкам сразу — одной строкой, на подпись свёрнутого списка.
 *
 * Разбор по источникам уехал под фолд, и вместе с ним уехало бы самое важное
 * предложение панели: «ступени набраны на разных окнах». Худший из источников и
 * есть общий ответ — сравнивать нельзя, если нельзя хотя бы у одного, — поэтому
 * строка берёт именно его, а не большинство и не первый.
 */
export function laddersWord(sources: DecidedSource[]): string {
  const bySource = sourcesOf(sources).map((src) => atSource(sources, src));
  if (bySource.length === 0) return "ступеней нет";
  const ladders = bySource.filter((rows) => rows.length > 1);
  if (ladders.length === 0) return "по одной ступени: сравнивать не с чем";
  const broken = ladders.filter((rows) => !sameWindows(rows));
  if (broken.length > 0) {
    return "ступени набраны на РАЗНЫХ окнах — сравнивать между собой нельзя";
  }
  return "ступени на одних окнах: разница между ними — ЦЕНА";
}

/** Цвет общего приговора лесенкам — по тому же правилу, что и у ladderTone. */
export function laddersTone(sources: DecidedSource[]): string {
  const ladders = sourcesOf(sources)
    .map((src) => atSource(sources, src))
    .filter((rows) => rows.length > 1);
  if (ladders.length === 0) return "var(--color-muted)";
  return ladders.every(sameWindows) ? "var(--color-good)" : "var(--color-warn)";
}

/** Набраны ли все ступени источника на одних и тех же окнах. */
function sameWindows(rows: DecidedSource[]): boolean {
  const first = rows[0];
  if (first === undefined) return true;
  return rows.every((r) => r.signals === first.signals);
}

/**
 * Что ворота согласия со стаканом сделали с деньгами — одной строкой.
 *
 * Приговор воротам — знак счёта придержанных, и под фолдом он лежит по строке
 * на ступень. Наверх выносится ХУДШАЯ половина: ворота, которые хоть где-то
 * стоят денег, — повод опустить порог, и прятать этот повод за клик нельзя.
 */
export function gatesWord(sources: DecidedSource[]): string {
  const held = sources.filter((s) => s.suppressed > 0);
  if (held.length === 0) return "ворота никого не придержали";
  const costly = held.filter((s) => (num(s.suppressed_pnl) ?? 0) > 0);
  if (costly.length > 0) {
    return `ворота стоят денег на ${costly.length} ступенях из ${held.length}`;
  }
  const saved = held.filter((s) => (num(s.suppressed_pnl) ?? 0) < 0);
  if (saved.length === 0) return "счёт придержанных ноль";
  return "ворота сэкономили";
}

/** Цвет общего приговора воротам — по тому же правилу, что и у gateTone. */
export function gatesTone(sources: DecidedSource[]): string {
  const held = sources.filter((s) => s.suppressed > 0);
  if (held.length === 0) return "var(--color-muted)";
  if (held.some((s) => (num(s.suppressed_pnl) ?? 0) > 0)) return "var(--color-warn)";
  if (held.some((s) => (num(s.suppressed_pnl) ?? 0) < 0)) return "var(--color-good)";
  return "var(--color-muted)";
}

/** Строки одной цены. */
export function atPrice(sources: DecidedSource[], price: string): DecidedSource[] {
  return sources.filter((s) => s.limit_price === price);
}

/**
 * Выигрыш модели по форе, секунды. null — сравнивать не с чем.
 *
 * Считается только когда ОБА источника набрали окна: разница медиан по разным
 * наборам окон мерила бы, какие окна кому достались, а не модель.
 *
 * Внутри ОДНОЙ ступени, а не по всей сводке. Сама фора от цены не зависит —
 * замок срабатывает одинаково, — но в сводке рядом с лесенкой живут и старые
 * одноступенчатые строки других суток, и медиана по объединению относилась бы к
 * выборке, которой не было.
 */
export function modelGain(sources: DecidedSource[]): number | null {
  const pm = sources.find((s) => s.source === "pm");
  const model = sources.find((s) => s.source === "model");
  if (!pm || !model || pm.signals === 0 || model.signals === 0) return null;
  return (model.lead_median_ms - pm.lead_median_ms) / 1000;
}

/**
 * Фора модели над рядом биржи одной фразой — по всей лесенке разом.
 *
 * Считается по ступеням, но печатается ОДНОЙ строкой, когда все ступени дали
 * одно число. Это не косметика: у настоящей лесенки фора обязана совпадать —
 * строки пишутся с одного решения, — и три одинаковых предложения подряд
 * читались бы как три независимых замера, то есть обещали бы подтверждение там,
 * где стоит одно наблюдение. Разошедшиеся ступени печатаются каждая своей: это
 * означает, что окна у них разные, и молчать об этом нельзя.
 */
export function modelGainWord(sources: DecidedSource[]): string | null {
  const gains = pricesOf(sources)
    .map((price) => ({ price, gain: modelGain(atPrice(sources, price)) }))
    .filter((g): g is { price: string; gain: number } => g.gain !== null);
  const first = gains[0];
  if (first === undefined) return null;
  if (gains.every((g) => g.gain === first.gain)) {
    return `Модель называет исход на ${first.gain.toFixed(1)} с раньше ряда биржи по медиане` +
      (gains.length > 1 ? " — одинаково на всех ступенях лесенки." : ".");
  }
  return gains
    .map((g) => `По ${g.price} модель называет исход на ${g.gain.toFixed(1)} с раньше ряда биржи.`)
    .join(" ");
}

/**
 * # Тейкерская половина — то, по чему принимают решение
 *
 * Мейкерская половина замера закончена, и закончена отрицательно: за сутки
 * налили 16 заявок из 1366, счёт по ним — четыре доллара. Это не «мало данных»,
 * а измеренный ответ: очередь в решённом окне до нас не доезжает. Тейкер же
 * входит, и вопрос к нему совсем другой — не «хорошо ли замок отбирает окна»
 * (`won`/`settled` по всем 1517 окнам), а «что вышло у того, кто вошёл» (118
 * входов). Знаменатели расходятся на порядок, и общая доля прячет нужную.
 *
 * Поэтому все функции ниже считают ТОЛЬКО по тейкерским входам и возвращают
 * null там, где входов не было: ноль здесь читался бы как измеренный результат.
 *
 * # И только по СВЕЖЕМУ стакану
 *
 * Второй разрез, без которого первый ничего не стоит. Теневой счёт растёт с
 * возрастом нашего снимка книги: на снимках моложе порога доходность около
 * нуля, на снимках в секунду-три — восемьдесят процентов. Второе невозможно
 * физически — покупка по ≤ 0.999 с выплатой $1 не даёт и процента, — и означает
 * ровно одно: цену взяли из книги, которой на бирже уже нет. 97 % всего
 * теневого счёта лежало на протухших снимках, и живьём это подтвердилось
 * буквально: девять настоящих исполнений дали −$10.47, а все три налива дешевле
 * 0.90 проиграли.
 *
 * Поэтому «цена входа», «кромка», «запас», «оборот», «счёт» и «доход» считаются
 * по СВЕЖЕЙ половине (`fresh_*`), а протухшая едет отдельной подписью мерой
 * артефакта. Числа `taker_*` из ответа никуда не делись и остаются суммой обеих
 * половин — по ним и считается доля свежих.
 */

/** Средняя цена входа по СВЕЖЕМУ стакану. null — таких входов не было. */
export function takerPrice(s: DecidedSource): number | null {
  if (s.fresh_settled === 0) return null;
  const sum = num(s.fresh_price_sum);
  if (sum === null) return null;
  return sum / s.fresh_settled;
}

/**
 * Кромка тейкера в процентных пунктах: угадано минус цена входа.
 *
 * Знак разности сам по себе НЕ доказывает кромку — рядом обязана стоять
 * z-оценка (`fresh_price_z`), и красится строка по ней, а не по этому числу.
 * Здесь оно ради масштаба: «+10 п.п.» отвечает на «сколько это в деньгах», чего
 * сигма не говорит.
 */
export function takerEdge(s: DecidedSource): number | null {
  const price = takerPrice(s);
  if (price === null) return null;
  return s.fresh_won / s.fresh_settled - price;
}

/**
 * Доходность вложенного доллара, доли единицы. null — оборота не было.
 *
 * Считается от ОБОРОТА, а не от бюджета: бюджет — это сколько мы готовы были
 * дать, оборот — сколько реально стоило купленное. На дешёвых входах они
 * расходятся втрое.
 */
export function takerRoi(s: DecidedSource): number | null {
  const quote = num(s.fresh_quote);
  const pnl = num(s.fresh_pnl);
  if (quote === null || pnl === null || quote === 0) return null;
  return pnl / quote;
}

/**
 * Доля свежих среди сведённых входов, доли единицы. null — входов не было.
 *
 * Знаменателем идут ВСЕ сведённые входы, а не только свежие: вопрос здесь
 * «сколько замера уцелело», и без общего числа доля не отвечает на него вовсе.
 */
export function freshShare(s: DecidedSource): number | null {
  if (s.taker_settled === 0) return null;
  return s.fresh_settled / s.taker_settled;
}

/**
 * Протухшая половина словами — мера артефакта для подписи под таблицей.
 *
 * Печатает счёт и цену входа по ней РЯДОМ, потому что порознь они выглядят
 * достижением: «+$745 при 78 угаданных из 78» читается как кромка ровно до
 * того мгновения, как рядом встанет цена входа 0.774 — по которой нам никто
 * ничего не продавал.
 */
export function staleWord(s: DecidedSource): string {
  if (s.stale_settled === 0) return "протухших входов не было — замер чист целиком";
  const sum = num(s.stale_price_sum);
  const price = sum === null ? null : sum / s.stale_settled;
  const pnl = num(s.stale_pnl) ?? 0;
  const priceWord = price === null ? "" : ` по цене ${price.toFixed(3)}`;
  return (
    `${hitWord(s.stale_won, s.stale_settled)}${priceWord}, ` +
    `счёт ${formatDollars(String(pnl))} — в результат НЕ входит`
  );
}

/**
 * Цвет доли уцелевшего замера.
 *
 * Красным не «убыток», а ПОТЕРЯ ЗАМЕРА: строка, где свежих входов не осталось
 * вовсе, ничего не измерила, и зелёный прочерк в «счёте» рядом с ней врал бы
 * не меньше прежних плюсов.
 */
export function staleTone(s: DecidedSource): string {
  const share = freshShare(s);
  if (share === null) return "var(--color-muted)";
  if (share === 0) return "var(--color-bad)";
  if (share < 0.5) return "var(--color-warn)";
  return "var(--color-good)";
}

/** Та же мера одной фразой на все ступени — для подписи свёрнутого блока. */
export function stalesWord(sources: DecidedSource[]): string {
  const settled = sources.reduce((a, s) => a + s.taker_settled, 0);
  if (settled === 0) return "входов ещё не было";
  const fresh = sources.reduce((a, s) => a + s.fresh_settled, 0);
  const stalePnL = sources.reduce((a, s) => a + (num(s.stale_pnl) ?? 0), 0);
  return (
    `свежих ${fresh} из ${settled} входов; ` +
    `на протухшем стакане отложено ${formatDollars(String(stalePnL))}`
  );
}

/** Цвет той же фразы: по доле уцелевшего замера на всех ступенях сразу. */
export function stalesTone(sources: DecidedSource[]): string {
  const settled = sources.reduce((a, s) => a + s.taker_settled, 0);
  if (settled === 0) return "var(--color-muted)";
  const fresh = sources.reduce((a, s) => a + s.fresh_settled, 0);
  if (fresh === 0) return "var(--color-bad)";
  if (fresh / settled < 0.5) return "var(--color-warn)";
  return "var(--color-good)";
}

/** Доля комиссии в счёте: на марже в несколько центов она решает исход. */
export function takerFeeShare(s: DecidedSource): number | null {
  const fee = num(s.taker_fee);
  const quote = num(s.taker_quote);
  if (fee === null || quote === null || quote === 0) return null;
  return fee / quote;
}

/**
 * Сколько входов ступень обязана набрать, прежде чем её доля угаданного
 * что-то докажет.
 *
 * # Зачем это число на экране
 *
 * Затем, что верхние ступени лесенки выглядят в таблице лучше всех и не значат
 * при этом ничего. Безубыток по доле угаданного на цене входа p равен САМОЙ p:
 * заплатив 0.999 за исход, надо угадывать 99.9 % окон, чтобы выйти в ноль. И
 * «угадано 20/20» на ступени 0.999 — это доля 100 % против безубытка 99.9 %,
 * то есть запас в одну десятую процента, померенный двадцатью наблюдениями.
 * В колонке «угадано» такая строка стои́т рядом со строкой 0.95, где те же
 * 20 из 20 означают запас в пять процентов, — и по колонке они неразличимы.
 *
 * # Откуда формула
 *
 * Запас объявляется от двух сигм (`zscore`), стандартная ошибка доли равна
 * sqrt(p(1-p)/n). Лучшее, на что ступень может рассчитывать, — БЕЗУПРЕЧНЫЙ
 * счёт, то есть наблюдённая доля 1. Тогда двум сигмам отвечает
 *
 *	1 - p >= 2*sqrt(p(1-p)/n)   что то же самое   n >= 4p/(1-p)
 *
 * Отсюда 76 входов на 0.95, 396 на 0.99, 796 на 0.995 и 3996 на 0.999. Это
 * НИЖНЯЯ граница: любой проигрыш поднимает её, и на дорогих ступенях резко —
 * один проигрыш по 0.999 стоит тысячи выигрышей.
 *
 * null означает «цену прочитать не вышло», а не «доказывать нечего».
 */
export function provingEntries(price: string): number | null {
  const p = num(price);
  if (p === null || p <= 0 || p >= 1) return null;
  return Math.ceil((4 * p) / (1 - p));
}

/**
 * То же словами и с уже набранной долей — для подписи под таблицей.
 *
 * Печатает ОБА числа, набранное и нужное: «12 из 796» отвечает на вопрос
 * «сколько ещё ждать» и одновременно не даёт прочитать дюжину входов как
 * измерение.
 */
export function provingWord(s: DecidedSource): string {
  const need = provingEntries(s.limit_price);
  if (need === null) return "";
  // Считается по СВЕЖИМ входам: доказывает кромку та же выборка, по которой её
  // и объявляют, а протухшие входы доказывают только возраст нашего снимка.
  if (s.fresh_settled >= need) {
    return `выборки хватает: ${s.fresh_settled} свежих входов при нужных ${need}`;
  }
  return (
    `для двух сигм нужно ${need} входов при безупречном счёте — ` +
    `набрано ${s.fresh_settled} свежих`
  );
}

/**
 * Потолок объёма словами.
 *
 * Ноль упёршихся входов — это НЕ «объёма нет», а «потолок не измерен»: на нашем
 * бюджете стакан ни разу не кончился, и сколько там было сверх него, мы не
 * видели. Прочитать это как измеренный потолок — ровно та ошибка, из-за которой
 * ставку масштабируют в пустоту, поэтому фраза говорит про бюджет, а не про
 * рынок.
 */
export function ceilingWord(s: DecidedSource): string {
  if (s.taker_feasible === 0) return "входов не было";
  if (s.taker_partial === 0) {
    return "на нашем бюджете стакан не кончался ни разу — потолок выше и не измерен";
  }
  return `в стакан упёрлись ${s.taker_partial} из ${s.taker_feasible}: здесь потолок и виден`;
}

/**
 * Что ответила ПРОБА глубины.
 *
 * Заведена ровно затем, чтобы `ceilingWord` перестал отвечать «не измерен».
 * Торговый бюджет упирался в себя, а не в стакан (`taker_partial` был нулём во
 * всех 134 входах замера), и потолок оставался неизвестен по построению: у
 * вопроса, который задают своими деньгами, ответ ограничен своими деньгами.
 *
 * Проба задаёт тот же вопрос бо́льшими деньгами и ничего не тратит. Ответов у
 * неё два, и они противоположны по смыслу:
 *
 *   - стакан КОНЧИЛСЯ раньше пробы — потолок измерен, и он равен взятому;
 *   - стакан выдержал — потолок всё ещё выше, но теперь известно «не меньше
 *     пробного бюджета», и ставку можно поднимать до него.
 */
export function depthWord(s: DecidedSource): string {
  if (s.probed === 0) return "пробы глубины не было";
  const budget = num(s.probe_budget);
  const quote = num(s.probe_quote);
  if (budget === null || quote === null || budget === 0) {
    return "пробы глубины не было";
  }
  const share = quote / budget;
  if (s.probe_partial > 0) {
    return (
      `проба уперлась в стакан в ${s.probe_partial} из ${s.probed}: ` +
      `взяла ${formatDollars(String(quote))} из ${formatDollars(String(budget))} — ` +
      `здесь потолок и ИЗМЕРЕН`
    );
  }
  return (
    `проба взяла ${formatDollars(String(quote))} из ` +
    `${formatDollars(String(budget))} (${(share * 100).toFixed(0)} %), ни разу не упёршись: ` +
    `потолок не ниже пробного бюджета`
  );
}

/**
 * Что придержали ВОРОТА согласия со стаканом и чего это стоило.
 *
 * Обе половины разреза в одной фразе намеренно. Правило, чью цену не видно,
 * снять нельзя: «ворота не пропустили ни одного проигрыша» и «ворота не
 * пропустили ничего» обязаны различаться на экране, а не только в базе.
 *
 * Знак счёта придержанных и есть приговор правилу: отрицательный — ворота
 * сэкономили ровно эту сумму, положительный — они её стоили, и порог пора
 * опускать.
 */
export function gateWord(s: DecidedSource): string {
  if (s.suppressed === 0) return "ворота никого не придержали";
  const pnl = num(s.suppressed_pnl);
  const held = `придержано ${s.suppressed}, из них выиграло бы ${s.suppressed_won}`;
  if (pnl === null || pnl === 0) return `${held}; счёт придержанных ноль`;
  if (pnl < 0) return `${held}; они стоили бы ${formatDollars(s.suppressed_pnl)} — ворота сэкономили`;
  return `${held}; они принесли бы ${formatDollars(s.suppressed_pnl)} — ворота стоят денег`;
}

/** Цвет приговора воротам: зелёный, когда придержанные были бы убыточны. */
export function gateTone(s: DecidedSource): string {
  if (s.suppressed === 0) return "var(--color-muted)";
  const pnl = num(s.suppressed_pnl);
  if (pnl === null || pnl === 0) return "var(--color-muted)";
  return pnl < 0 ? "var(--color-good)" : "var(--color-warn)";
}

/** Доля словами: «47/50». Прочерк — входов не было, а не «ноль угадано». */
export function hitWord(won: number, settled: number): string {
  if (settled === 0) return "—";
  return `${won}/${settled}`;
}

/** Проценты со знаком. null — считать было не из чего. */
export function pct(v: number | null): string {
  if (v === null) return "—";
  const sign = v > 0 ? "+" : v < 0 ? "−" : "";
  return `${sign}${Math.abs(v * 100).toFixed(1)} %`;
}

/**
 * Приговор разделу одной строкой.
 *
 * Пишется от ТЕЙКЕРСКОГО входа, а не от числа отобранных окон. «733 окна,
 * мейкером 654» — правда, которая вводит в заблуждение: заявок налили 16, и
 * подзаголовок обещал бы шестьсот сделок там, где их шестьдесят одна. Ноль
 * входов при этом остаётся сказанным вслух: это результат замера, а не пустота.
 */
export function verdict(sources: DecidedSource[]): string {
  if (sources.length === 0 || sources.every((s) => s.signals === 0)) {
    return "окон ещё не отобрано";
  }
  // ПО КАЖДОЙ ЦЕНЕ отдельно, и это не многословие. Цена входа — ручка
  // оператора; свод по обеим сразу склеил бы опыт с его контролем в одно число,
  // и смена цены перестала бы быть видна на экране вовсе.
  return pricesOf(sources)
    .map((price) => {
      const rows = atPrice(sources, price);
      const total = rows.reduce((a, s) => a + s.signals, 0);
      const taker = rows.reduce((a, s) => a + s.taker_feasible, 0);
      if (taker === 0) return `по ${price} — ${total} окон, тейкером ни разу`;
      // Приговор — по СВЕЖЕЙ половине, как и вся таблица: на протухшем снимке
      // доля угаданного близка к единице по построению, и заголовок панели
      // обещал бы кромку, которой при живом ордере не будет.
      const won = rows.reduce((a, s) => a + s.fresh_won, 0);
      const settled = rows.reduce((a, s) => a + s.fresh_settled, 0);
      const pnl = rows.reduce((a, s) => a + (num(s.fresh_pnl) ?? 0), 0);
      const quote = rows.reduce((a, s) => a + (num(s.fresh_quote) ?? 0), 0);
      if (settled === 0) {
        const all = rows.reduce((a, s) => a + s.taker_settled, 0);
        // Разные ответы, и слить их нельзя: «исход ещё не известен» — это ждать,
        // «все входы на протухшем стакане» — это НЕ ждать, там мерить нечего.
        return all === 0
          ? `по ${price} — тейкером ${taker}, исход ещё не известен`
          : `по ${price} — тейкером ${taker}, но все ${all} входов на протухшем стакане`;
      }
      // Счёт ВМЕСТЕ с оборотом: сумма без него не отличает удачу на большом
      // объёме от кромки на малом.
      const roi = quote === 0 ? "" : ` (${pct(pnl / quote)} на обороте)`;
      return `по ${price} — тейкером ${taker} из ${total} окон, на свежем стакане ` +
        `угадано ${hitWord(won, settled)}, ${formatDollars(String(pnl))}${roi}`;
    })
    .join(" · ");
}

/**
 * Наблюдение ленты словами: налили бы нас или нет.
 *
 * Три состояния, и все три разные. «Не наблюдали» — трейдер перезапустился
 * посреди окна; «никто не продал» — самый частый честный ответ; «прошло, но
 * досталось бы меньше» — очередь впереди съела объём. Свести их к одному числу
 * значило бы потерять то единственное, что здесь измеряется.
 */
export function fillWord(s: DecidedSource): string {
  if (s.observed === 0) return "ленту не наблюдали";
  const tape = num(s.tape_shares) ?? 0;
  const fill = num(s.fill_shares) ?? 0;
  if (tape === 0) return `по нашей цене не продал никто (${s.observed} окон)`;
  if (fill === 0) {
    return `прошло ${shares(s.tape_shares)} share, но очередь впереди съела всё`;
  }
  return `прошло ${shares(s.tape_shares)} share, нам досталось бы ${shares(s.fill_shares)}`;
}

/**
 * Теневой счёт с ЗНАМЕНАТЕЛЕМ: сумма и по скольким окнам она посчитана.
 *
 * Ноль в счёте бывает двух родов, и путать их дорого. «Заявка стояла, никто не
 * налил» — это РЕЗУЛЬТАТ замера, самый частый честный ответ на вопрос «сколько
 * даёт мейкер по 0.95 в решённом окне». «Считать было нечем» — заявку нельзя
 * было поставить либо ленту не наблюдали, и никакого утверждения о деньгах
 * здесь нет.
 *
 * Сумма складывает их в одно число, поэтому нулевой знаменатель даёт ПРОЧЕРК, а
 * не «$0.0»: то же правило, по которому у копирования прочерк в деньгах
 * означает «считать было не по чему».
 */
export function pnlWord(sum: string, priced: number): string {
  if (priced === 0) return "—";
  return `${formatDollars(sum)} (${priced})`;
}

/** Тон числа: положительное — зелёное, отрицательное — красное, ноль — серое. */
export type Tone = "pos" | "neg" | "flat";

export function tone(v: string | null | undefined): Tone {
  const n = num(v);
  if (n === null || n === 0) return "flat";
  return n > 0 ? "pos" : "neg";
}

export function toneColor(t: Tone): string {
  if (t === "pos") return "text-[var(--color-bid)]";
  if (t === "neg") return "text-[var(--color-ask)]";
  return "text-[var(--color-muted)]";
}

/**
 * Строка ленты: что стало с планом.
 *
 * Отказ едет ПРИЧИНОЙ, а не пустотой. У одного из двух планов она стоит почти
 * всегда по построению — заявка по нашей цене законна лишь пока лучшее
 * предложение выше неё, а ровно тогда тейкеру нечего забирать, — и пустое место
 * читалось бы как «не считали».
 */
export function planWord(p: DecidedSignal["maker"] | DecidedSignal["taker"]): string {
  if (p.reason) return p.reason;
  if (!p.size) return "—";
  const parts = [`${shares(p.size)} share`];
  if (p.queue_ahead && num(p.queue_ahead)) {
    parts.push(`впереди ${shares(p.queue_ahead)}`);
  }
  if (p.levels && p.levels > 1) parts.push(`${p.levels} уровня`);
  if (p.partial) parts.push("стакан кончился раньше денег");
  return parts.join(", ");
}

/**
 * Рынок строки: актив и ДЛИТЕЛЬНОСТЬ ОКНА.
 *
 * Длительность из slug, а не `window_s` из строки. Это разные величины, и путать
 * их дорого: `window_s` — ширина метрики резолва (сейчас шестьдесят секунд у
 * всех серий сразу), а длительность окна — пять минут или пятнадцать. Показав
 * первое, лента рисовала бы «BTC 60s» и там и там — два разных рынка,
 * сработавших на одном тике, выглядели бы одной строкой, задвоенной по ошибке.
 *
 * Пустой slug оставляет актив: рынок вне серии тоже бывает, и молчать про него
 * хуже, чем сказать половину.
 */
export function marketWord(s: DecidedSignal): string {
  const parts = s.slug ? parseSeriesSlug(s.slug) : null;
  if (!parts) return s.asset ?? "—";
  return `${parts.asset} ${parts.duration}`;
}

/** Исход строки словами. Прочерк у несведённых — это не проигрыш. */
export function outcomeWord(s: DecidedSignal): string {
  if (!s.settled_at) return "ждёт исхода";
  if (!s.resolved) return "исход не подтверждён";
  return s.won ? "угадали" : "промах";
}
